import { useState } from 'react';
import { EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { ComicCard as ComicCardData } from '@/sources/types';
import { ComicCard } from '@/components/ComicCard';
import { useBlacklist } from './useBlacklist';
import { cn } from '@/lib/utils';

export interface BlockedComicCardProps {
  comic: ComicCardData;
  className?: string;
}

/**
 * Substitute card for a blocked comic — opaque placeholder that lists the
 * matching tags and offers a "show anyway" reveal. Drop in instead of the
 * raw `<ComicCard>` when wiring up source list pages with
 * {@link useFilteredComics}.
 */
export function BlockedComicCard({ comic, className }: BlockedComicCardProps) {
  const { matchedTags } = useBlacklist();
  const [revealed, setRevealed] = useState(false);
  const matched = matchedTags(comic);

  if (revealed) {
    return <ComicCard comic={comic} className={className} />;
  }

  return (
    <div
      className={cn(
        'flex aspect-[3/4] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border/60 bg-muted/30 p-3 text-center text-xs text-muted-foreground',
        className,
      )}
    >
      <EyeOff className="h-5 w-5 opacity-50" />
      <span className="font-medium">Blocked</span>
      {matched.length > 0 && (
        <span className="line-clamp-2 text-[10px] opacity-70">{matched.join(', ')}</span>
      )}
      <div className="flex flex-col gap-1 text-[10px]">
        <button
          type="button"
          className="underline opacity-70 hover:opacity-100"
          onClick={() => setRevealed(true)}
        >
          Show anyway
        </button>
        <Link
          to={`/source/${comic.source}/comic/${encodeURIComponent(comic.id)}`}
          className="underline opacity-50 hover:opacity-100"
        >
          Open details
        </Link>
      </div>
    </div>
  );
}
